import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Editor from 'react-quill';

import useToggle from '@hooks/useToggle';
import Checkbox from '@components/dataEntry/checkbox/Checkbox';
import styled, { css } from 'styled-components';
import Button from '@components/dataEntry/button/Button';
import { useRecoilValue } from 'recoil';
import { currentUserIDState } from '@atom/setting/user';
import rollingDice from '@utils/rollingDice';
import {
  COMMENT_TYPE,
  COMMENT_TYPE_VALUE,
} from '@typings/common/commentType.enum';
import { updateComment } from '@store/firebase/comment/update-comment';
import { QuillEditorContainer } from './QuillEditorStyle';

import {
  customEmojiModule,
  imageHandler,
  mentionModule,
} from './CustomHandler/CustomHandler';
import { ACCESS_TYPE } from '@typings/common/accessType';
import { updateGuestbook } from '@store/firebase/guestBook/update-guestbook';
import { cookieStore } from '@store/cookie/cookieStore';
import TextInput from '@components/dataEntry/Input/TextInput';
import { useInput, useInputs } from '@hooks/useInputs';
import BattleMode, {
  AttatchmentWrapper,
} from './CommentAttatchment/BattleMode/BattleMode';
import { randomSelection } from '@utils/rockScissorsPaper';
import { BATTLE_MODE, BATTLE_MODE_VALUE } from '@typings/common/battleType';
import { addNotification } from '@store/firebase/notification/add-notification';
import { NOTI_TYPE } from '@typings/notification/notificationType';
import { masterSet } from '@masterSetting/masterSet/masterSetting';
import { IcommentItem } from '@typings/common/comment.interface';
import {
  CHAT_ROOM_TYPE_VALUE,
  CHAT_TYPE,
  IChat,
} from '@typings/chat/chat.interface';
import { addChat } from '@store/firebase/chat/add-chat';
import { handleEnterKeyPress } from '@utils/handleKeypress.function';
import { NOTI_TYPE_VALUE } from '@typings/notification/notificationType';
import CustomToolBar, { IsetToolbar } from './Toolbar/CustomToolBar';
import icons from '@components/dataDisplay/Icons/Icons';
import useImagePasteHandler from '@hooks/usePasteFileHandler';
import { useTwitterEmbedPaste } from '@hooks/useTwitterEmbedPaste';
import '../QuillWidgetEmbed/XWidgetBlots';
import { syncQuillParagraphClasses } from './QuillParts/syncQuillParagraphClasses';
import ItemSelector from '@components/dataEntry/select/ItemSelector';
import { mainPhotocardDataState } from '@atom/setting/photocardAutoSetState';
import {
  parchaseItem,
  presentItem,
  getItemData,
} from '@store/firebase/store/parchase-item';
import { presentSkill } from '@store/firebase/skill/update-skill';
import { ITEM_FUNCTION_TPYE } from '@typings/store/storeItemFunctionType';
import { BOARD_TYPE } from '@typings/boardType';
import { CollectionPath } from '@typings/collections.enum';
import { fuego } from '@nandorojo/swr-firestore';
import { timelineCommentSettingState } from '@atom/setting/timelineCommentSetting';
import { processTimelineNumberTags } from '@utils/timelineNumberLink.util';
import { registerQuillModules } from './QuillParts/QuillRegistry';

export const initialToolbar = {
  size: false,
  align: false,
  color: false,
  list: false,
  blockquote: false,
  codeblock: false,
  link: false,
  file: false,
  html: false,
  image: true,
  emoji: false,
  video: true,
};

const AddCommentWrap = styled.div`
  width: 100%;
`;

const ButtonWrap = styled.div`
  display: flex;
  gap: 4px;
`;

const typeMap = {
  editor: css``,
  input: css`
    border: 1px solid var(--lineColor);
    border-radius: var(--cardBorderRadius);
    padding: 8px; ;
  `,
};

const EditorWrap = styled.div<{
  type?: 'editor' | 'input';
}>`
  width: 100%;
  ${({ type }) => type && typeMap[type]}
`;

const CheckboxValue = styled.span`
  font-size: 11px;
`;

export const ControlSec = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;

  @media (max-width: 480px) {
    /* flex-direction: column; */
  }
`;

const FrontBox = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const BackBox = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;

const ControlBox = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 2px 8px;
`;

const CharacterCountWrapper = styled.div<{ isOverLimit?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: ${({ isOverLimit }) => (isOverLimit ? 'var(--errorColor, #ff4444)' : 'var(--subTextColor)')};
  margin-right: 8px;
`;

const CharacterCount = styled.span<{ isOverLimit?: boolean }>`
  font-weight: ${({ isOverLimit }) => (isOverLimit ? '600' : '400')};
`;

interface Props {
  data?: IcommentItem;
  value?: string | undefined;
  setToolbar: IsetToolbar;
  onChange?: (content: string) => void;
  style?: React.CSSProperties;
  files?: [];
  setFile?: boolean;
  onFileChange?: (file: any) => void;
  placeholder?: string;
  type?: 'editor' | 'input';
  commentSetting?: {
    secretCheckbox?: boolean;
    dice?: boolean;
    battle?: boolean;
    divider?: boolean;
    collapseComment?: boolean;
    hashtag?: boolean;
    luckyDraw?: boolean; // 뽑기 기능
    showCharacterCount?: boolean; // 글자수 표시 override
    maxCharacterCount?: number;   // 글자수 제한 override
    battleMode?: BATTLE_MODE_VALUE; // 배틀 모드 override
  };
  useGlobalCommentSetting?: boolean;
  collectionPath: string;
  maxHeight?: number;
  minHeight?: number;
  noControls?: boolean;
  isEdit?: boolean;
  cancelEdit?: () => void;
  confirmEdit?: () => void;
  id: string;
  boardId?: string;
  boardAuthorUid?: string;
  submitText?: string;
  submitIcon?: keyof typeof icons;
  handleSubmit?: () => void;
  allowAnonymous?: boolean;
  commentAuthor?: string;
  pStyle?: CSSProperties;
  setImgURLList?: any;
  isGmSpeak?: boolean;
  customCommentType?: COMMENT_TYPE_VALUE;
  chatType?: CHAT_ROOM_TYPE_VALUE;
  participants?: string[];
  dir?: 'flex-start' | 'center' | 'flex-end';
  buttonSize?: 'sm' | 'md';
  sendToMastersAlert?: boolean;
  newLineDisabled?: boolean;
  commentId?: string;
  guestbook?: boolean;
  chat?: boolean;
  notiType?: NOTI_TYPE_VALUE;
  close?: () => void;
  toolbarRows?: 1 | 2;
  disableTwitterEmbed?: boolean;
  preventAutoFocusOnMount?: boolean;

  // 🎯 커스텀 제출 핸들러 (있으면 기본 로직 대신 실행)
  onCustomSubmit?: (
    content: string,
    options: {
      isSecret: boolean;
      isAddDice: boolean;
      dice?: [number, number];
      isBattleMode: boolean;
      battleMode: BATTLE_MODE_VALUE;
      myBattleResult?: string;
      battleOpponentId?: string;
      commentCollapseText?: string;
      isCommentCollapse: boolean;
      commentImgURLList: string[];
      commentType: COMMENT_TYPE_VALUE;
    },
  ) => Promise<void>;
}

const CommentQuill = ({
  value,
  onChange,
  setToolbar,
  placeholder = '텍스트를 입력해주세요.',
  type = 'input',
  commentSetting = {
    secretCheckbox: true,
    dice: true,
    battle: false,
    collapseComment: masterSet.commentMode?.collapse || false,
    hashtag: false,
    luckyDraw: false,
  },
  useGlobalCommentSetting = true,
  collectionPath,
  maxHeight,
  minHeight,
  noControls = false,
  isEdit,
  cancelEdit,
  boardId,
  commentId,
  boardAuthorUid,
  id,
  data,
  style,
  submitText,
  submitIcon,
  commentAuthor,
  allowAnonymous,
  setImgURLList,
  isGmSpeak,
  customCommentType,
  guestbook,
  chat,
  participants,
  dir = 'flex-start',
  buttonSize,
  sendToMastersAlert,
  newLineDisabled = false,
  notiType,
  close,
  toolbarRows = 1,
  disableTwitterEmbed = false,
  preventAutoFocusOnMount = true,
  onCustomSubmit, // 🎯 커스텀 제출 핸들러
}: Props) => {
  useMemo(() => {
    registerQuillModules();
    return true;
  }, []);

  const currentUserID = useRecoilValue(currentUserIDState);
  const representativeCharacter = useRecoilValue(mainPhotocardDataState);
  const cashedUserId = cookieStore.getUserToken();
  const quillRef = useRef<Editor | null>(null);
  const isComposingRef = useRef(false);
  const pendingComposedValueRef = useRef<string | null>(null);

  // 📍 타임라인 댓글 전역 설정 (서버에서 한 번만 로드, 컴포넌트별 설정보다 우선)
  const globalCommentSetting = useRecoilValue(timelineCommentSettingState);

  // 📍 전역 설정과 컴포넌트 설정 병합 (전역 설정이 false면 컴포넌트 설정도 false로 강제)
  // guestbook 모드에서는 타임라인 전역 설정을 무시하고 commentSetting을 직접 사용
  const mergedCommentSetting = useMemo(() => {
    if (!useGlobalCommentSetting) {
      return {
        secretCheckbox: commentSetting.secretCheckbox ?? true,
        dice: commentSetting.dice ?? false,
        battle: commentSetting.battle ?? false,
        collapseComment: commentSetting.collapseComment ?? false,
        hashtag: commentSetting.hashtag ?? false,
        luckyDraw: commentSetting.luckyDraw ?? false,
        showCharacterCount: commentSetting.showCharacterCount ?? false,
        maxCharacterCount: commentSetting.maxCharacterCount ?? 10000,
      };
    }

    return {
      secretCheckbox: guestbook
        ? (commentSetting.secretCheckbox ?? true)
        : globalCommentSetting.useSecret ? (commentSetting.secretCheckbox ?? true) : false,
      dice: guestbook
        ? (commentSetting.dice ?? false)
        : globalCommentSetting.useDice ? (commentSetting.dice ?? true) : false,
      battle: guestbook
        ? (commentSetting.battle ?? false)
        : globalCommentSetting.useBattle ? (commentSetting.battle ?? false) : false,
      collapseComment: guestbook
        ? (commentSetting.collapseComment ?? false)
        : globalCommentSetting.useCollapseComment ? (commentSetting.collapseComment ?? false) : false,
      hashtag: commentSetting.hashtag ?? false,
      luckyDraw: guestbook
        ? (commentSetting.luckyDraw ?? false)
        : globalCommentSetting.useLuckyDraw ? (commentSetting.luckyDraw ?? false) : false,
      showCharacterCount: commentSetting.showCharacterCount ?? (globalCommentSetting.showCharacterCount ?? false),
      maxCharacterCount: commentSetting.maxCharacterCount ?? (globalCommentSetting.maxCharacterCount ?? 10000),
    };
  }, [globalCommentSetting, commentSetting, guestbook, useGlobalCommentSetting]);

  // 📍 툴바 설정도 전역 설정과 병합 (이미지, 동영상, 이모티콘)
  const mergedToolbar = useMemo(() => {
    if (!useGlobalCommentSetting) {
      return { ...setToolbar };
    }

    return {
      ...setToolbar,
      image: globalCommentSetting.useImage ? (setToolbar.image ?? true) : false,
      video: globalCommentSetting.useVideo ? (setToolbar.video ?? true) : false,
      emoji: globalCommentSetting.useEmoticon ? (setToolbar.emoji ?? false) : false,
    };
  }, [globalCommentSetting, setToolbar, useGlobalCommentSetting]);

  const currentBattleMode = useMemo<BATTLE_MODE_VALUE>(() => {
    if (commentSetting.battleMode) return commentSetting.battleMode;
    return globalCommentSetting.battleMode || BATTLE_MODE.INSTANT;
  }, [commentSetting.battleMode, globalCommentSetting.battleMode]);

  // 🎁 랜덤박스 관련 state
  const [isUseLuckyDraw, setIsUseLuckyDraw] = useState(false);
  const [selectedLuckyDrawId, setSelectedLuckyDrawId] = useState<string>('');

  // 🏪 Store 게시판 존재 여부 확인
  const hasStoreBoard = useMemo(() => {
    return masterSet.boardSet?.some(
      (board) => board.typeName === BOARD_TYPE.STORE,
    );
  }, []);

  // 익명 처리 ---------------------------------------------------
  const [
    { anonymousName, anonymousPassword },
    handleAnonymousName,
    resetAnonymous,
  ] = useInputs({
    anonymousName: cookieStore.getAnonymousName() || '',
    anonymousPassword: cookieStore.getAnonymousPassword() || '',
  });
  // 익명 처리 end---------------------------------------------------

  // 댓글에 첨부된 이미지 리스트
  const [commentImgURLList, setCommentImgURLList] = useState([]);

  const [addLoading, setAddLoading] = useState(false);
  const [isSecret, toggleSecretChange, secretCheckReset] = useToggle(
    isEdit && data ? data.accessType === ACCESS_TYPE.SECRET : false,
  );
  const [comment, setComment] = useState(isEdit && data ? data.contents : '');

  // 🔒 실시간 업데이트로 boardId prop이 바뀌어도 제출 대상 포스트가 바뀌지 않도록 고정
  // 댓글 작성 중(comment가 비어있지 않을 때)에는 boardId를 업데이트하지 않음
  const stableBoardIdRef = useRef<string | undefined>(boardId);
  useEffect(() => {
    if (boardId && !comment) {
      stableBoardIdRef.current = boardId;
    }
  }, [boardId, comment]);

  const [commentType, setCommentType] = useState<COMMENT_TYPE_VALUE>(
    customCommentType || COMMENT_TYPE.DEFALUT,
  );
  const [isAddDice, toggleDiceChange, diceCheckReset] = useToggle(false);
  const [isHashtag, toggleHashtag, hashtagReset] = useToggle(false);
  const [myBattleResult, setBattleResult] = useState<any>('');
  const [battleOpponentId, setBattleOpponentId] = useState<string>('');
  const [isBattleMode, setIsBattleMode] = useState(false);
  const toggleBattleChange = () => {
    setCommentType(!isBattleMode ? COMMENT_TYPE.BATTLE : COMMENT_TYPE.DEFALUT);
    setIsBattleMode(!isBattleMode);
  };
  const [isCommentCollapse, handleCommentCollapseChange, commentCollapseReset] =
    useToggle(isEdit && data ? data.isCommentCollapse! : false);
  const [
    commentCollapseText,
    handleCommentCollapseTextChange,
    commentCollapseTextReset,
  ] = useInput(isEdit && data ? data.commentCollapseText : '');

  // 📍 글자수 계산 (HTML 태그 제거 후 순수 텍스트 길이)
  const currentCharacterCount = useMemo(() => {
    const content = value || comment || '';
    // HTML 태그 제거
    const textContent = content.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ');
    return textContent.length;
  }, [value, comment]);

  // 📍 글자수 제한 초과 여부
  // 채팅 입력창은 게시판 댓글 제한과 분리하여 글자수 제한을 적용하지 않는다.
  const isOverLimit = useMemo(() => {
    if (chat) return false;
    return currentCharacterCount > mergedCommentSetting.maxCharacterCount;
  }, [chat, currentCharacterCount, mergedCommentSetting.maxCharacterCount]);

  // 이미지 복사/붙여넣기로 넣기
  useImagePasteHandler({ quillRef, collectionPath: 'posting', setImgURLList });
  // 위젯넣기 - 댓글에서는 비활성화
  useTwitterEmbedPaste({
    quillRef,
    value,
    disableTwitterEmbed: disableTwitterEmbed || false,
  });

  // CSS 선택자에서 사용 불가능한 문자(/, #, . 등)를 언더스코어로 치환
  const safeId = useMemo(
    () => (id || '').replace(/[^a-zA-Z0-9_-]/g, '_') || 'comment-editor',
    [id],
  );

  const applyCommentChange = useCallback(
    (content: string) => {
      if (onChange) {
        onChange(content);
        return;
      }
      setComment(content);
    },
    [onChange],
  );

  const handleEditorChange = useCallback(
    (content: string, _delta?: unknown, source?: string) => {
      // Quill silent 변경은 react-quill이 editor-change로 전달함. 멘션 insertItem의 deleteText(silent)가
      // 부모 value를 빈 단락으로 덮어쓰지 않도록 여기서 state 반영을 막는다.
      if (source === 'silent') {
        return;
      }
      if (isComposingRef.current) {
        pendingComposedValueRef.current = content;
        return;
      }
      applyCommentChange(content);
    },
    [applyCommentChange],
  );

  useEffect(() => {
    let rafId = 0;
    let observer: MutationObserver | null = null;
    let quillInstance: any = null;
    let retries = 0;
    const maxRetries = 30;

    const scheduleParagraphClassSync = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        if (quillInstance?.root) {
          syncQuillParagraphClasses(quillInstance.root as HTMLElement);
        }
      });
    };

    const handleCompositionStart = () => {
      isComposingRef.current = true;
    };

    const handleCompositionEnd = () => {
      isComposingRef.current = false;
      if (pendingComposedValueRef.current !== null) {
        const bufferedValue = pendingComposedValueRef.current;
        pendingComposedValueRef.current = null;
        applyCommentChange(bufferedValue);
      }
    };

    const bindEditor = () => {
      try {
        quillInstance = quillRef.current?.getEditor?.();
      } catch (error) {
        quillInstance = null;
      }

      if (!quillInstance?.root) {
        if (retries < maxRetries) {
          retries += 1;
          rafId = requestAnimationFrame(bindEditor);
        }
        return;
      }

      const root = quillInstance.root as HTMLElement;
      root.addEventListener('compositionstart', handleCompositionStart);
      root.addEventListener('compositionend', handleCompositionEnd);
      quillInstance.on?.('text-change', scheduleParagraphClassSync);
      observer = new MutationObserver(scheduleParagraphClassSync);
      observer.observe(root, { childList: true, subtree: true });
      scheduleParagraphClassSync();
    };

    bindEditor();

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (observer) observer.disconnect();
      if (quillInstance?.off) {
        quillInstance.off('text-change', scheduleParagraphClassSync);
      }
      const root = quillInstance?.root as HTMLElement | undefined;
      if (root) {
        root.removeEventListener('compositionstart', handleCompositionStart);
        root.removeEventListener('compositionend', handleCompositionEnd);
      }
    };
  }, [applyCommentChange]);

  const modules = useMemo(
    () => ({
      keyboard: {
        bindings: {
          enter: {
            key: 13,
            handler: () => {
              return !newLineDisabled;
            },
          },
          shiftEnter: {
            key: 13,
            shiftKey: true,
            handler: () => {
              return true;
            },
          },
        },
      },
      autoLinks: true,
      mention: mentionModule,
      customEmoji: customEmojiModule,
      toolbar: {
        container: `#${safeId}`,
        handlers: {
          XEmbedBlot: function () { },
          image: function () {
            imageHandler({
              quill: this.quill,
              setImgURLList: setImgURLList || setCommentImgURLList,
              collectionPath: `${collectionPath}Comment`,
            });
          },
          // VideoInsertDropdown에서 직접 처리하므로 기본 EmbedURL 다이얼로그 차단
          video: function () { },
        },
      },
    }),
    [safeId, collectionPath, setImgURLList, newLineDisabled],
  );

  const reset = useCallback(() => {
    setComment('');
    diceCheckReset();
    secretCheckReset();
    commentCollapseReset();
    commentCollapseTextReset();
    if (close) close(); // 롤플레이형 모달 띄울 때, 작성 후 모달 닫기
    if (commentType !== COMMENT_TYPE.GM) setCommentType(COMMENT_TYPE.DEFALUT);
    if (cancelEdit) cancelEdit();

    // battle 관련 reset
    setIsBattleMode(false);
    setBattleResult('');
    setBattleOpponentId('');

    // 랜덤박스 관련 reset
    setIsUseLuckyDraw(false);
    setSelectedLuckyDrawId('');
  }, [commentType, cancelEdit, close]);

  const handleUpdateComment = useCallback(async () => {
    if (!allowAnonymous && !currentUserID) {
      // 익명허용도 아니고 로그인 안되어 있을 때
      if (confirm(`로그인 후 이용해 주세요.`)) {
        return;
      } else return;
    }
    setAddLoading(true);

    // dice
    const dice1: number = isAddDice ? rollingDice() : 1;
    const dice2: number = isAddDice ? rollingDice() : 1;

    // battle
    const isChallengeBattle = isBattleMode && currentBattleMode === BATTLE_MODE.CHALLENGE;
    const opponentBattleResult = isBattleMode && !isChallengeBattle ? randomSelection() : '';

    // 🎁 랜덤박스 오픈 처리
    let luckyDrawResult:
      | {
        randomBoxId: string;
        randomBoxTitle: string;
        resultItemId: string;
        resultItemTitle?: string;
        resultItemImgURL?: string;
        openedAt: Date;
      }
      | undefined = undefined;

    if (isUseLuckyDraw && selectedLuckyDrawId && representativeCharacter?.id) {
      try {
        // 1. 대표 캐릭터의 인벤토리에서 랜덤박스 아이템 가져오기
        const inventoryRef = fuego.db
          .collection(CollectionPath.PHOTOCARD)
          .doc(representativeCharacter.id)
          .collection('inventory')
          .doc(selectedLuckyDrawId);

        const randomBoxInventoryItem = await inventoryRef
          .get()
          .then((doc) => doc.data());

        if (randomBoxInventoryItem && randomBoxInventoryItem.randomBoxData) {
          const { itemList, skillList } = randomBoxInventoryItem.randomBoxData;
          const allItems = [...(itemList || []), ...(skillList || [])];

          if (allItems.length > 0) {
            // 2. 랜덤 아이템 선정
            const randomIndex = Math.floor(Math.random() * allItems.length);
            const pickItem = allItems[randomIndex];
            const isSkillItem = (skillList || []).includes(pickItem);

            let resultItemData = await getItemData({ itemID: pickItem });

            if (!resultItemData && isSkillItem) {
              const skillDoc = await fuego.db
                .collection(CollectionPath.SKILLS)
                .doc(pickItem)
                .get()
                .then((doc) => (doc.exists ? doc.data() : null));
              resultItemData = skillDoc || null;
            }

            if (resultItemData) {
              if (isSkillItem) {
                await presentSkill({
                  collectionPath: CollectionPath.PHOTOCARD,
                  docId: representativeCharacter.id,
                  skillIDList: [pickItem],
                });
              } else {
                // 2-1. 랜덤 뽑기 완료된 아이템을 인벤토리에 추가
                await parchaseItem({
                  collectionPath: CollectionPath.PHOTOCARD,
                  photocardID: representativeCharacter.id,
                  data: { ...resultItemData, price: 0, id: pickItem },
                  amount: 1,
                  buyerID: currentUserID || '',
                });
              }

              // 3. 댓글에 결과 아이템 정보 저장
              luckyDrawResult = {
                randomBoxId: selectedLuckyDrawId,
                randomBoxTitle: randomBoxInventoryItem.title || '',
                resultItemId: pickItem,
                resultItemTitle: resultItemData.title,
                resultItemImgURL: resultItemData.imgURL || '',
                openedAt: new Date(),
              };

              // 랜덤박스 아이템 인벤토리에서 제거
              await presentItem({
                collectionPath: CollectionPath.PHOTOCARD,
                giftingCharID: representativeCharacter.id,
                data: randomBoxInventoryItem,
                amount: 1,
              });
            }
          }
        }
      } catch (error) {
        console.error('댓글 랜덤박스 오픈 실패:', error);
        alert('랜덤박스 오픈 중 오류가 발생했습니다.');
        setAddLoading(false);
        return;
      }
    }

    // 🔒 실시간 업데이트로 boardId prop이 바뀐 경우에도 작성 시작 시점의 포스트로 제출
    const resolvedBoardId = stableBoardIdRef.current || boardId || '';

    // #N 타임라인 링크 칩 치환 (수정 모드에서는 기존 칩 보존)
    const processedComment = await processTimelineNumberTags(comment, collectionPath, !!isEdit);

    const commonNewData = {
      contents: processedComment,
      authorUid: commentAuthor || currentUserID,
      accessType: isSecret ? ACCESS_TYPE.SECRET : ACCESS_TYPE.PUBLIC,
      dice: isAddDice ? [dice1, dice2] : [],
      type: commentType || COMMENT_TYPE.DEFALUT,
      commentImgURLList,
      isGmSpeak: isGmSpeak || false,
      boardAuthorUid: boardAuthorUid || '',
      boardId: resolvedBoardId,
      myBattleResult: myBattleResult || '',
      opponentBattleResult: opponentBattleResult || '',
      battleOpponentId: battleOpponentId || '',
      ...(isBattleMode && { battleMode: currentBattleMode }),
      commentCollapseText: commentCollapseText || '',
      isCommentCollapse: isCommentCollapse || false,
      ...(luckyDrawResult && { luckyDrawResult }),
    };

    if (currentUserID) {
      reset();
      await updateComment({
        data: { ...commonNewData },
        commentId: commentId || '',
        collectionPath,
        guestbook,
        sendToMasters: sendToMastersAlert,
        skipCommentNotification: isChallengeBattle,
      });

      if (isChallengeBattle && battleOpponentId && battleOpponentId !== currentUserID) {
        addNotification({
          data: {
            contents: comment,
            createdAt: new Date(),
            sender: currentUserID,
            receiver: battleOpponentId,
            postId: resolvedBoardId,
            collectionPath,
            is_seen: false,
            type: NOTI_TYPE.BATTLE,
          },
          receiver: battleOpponentId,
        });
      }

      setAddLoading(false);
      return;
    }

    // 익명 게스트북일 때 && 롤플레잉도 겸용
    if (allowAnonymous && guestbook) {
      if (!anonymousName || !anonymousPassword) {
        alert('익명 닉네임 또는 비밀번호를 입력해 주세요.');
        setAddLoading(false);
        return;
      }
      if (
        anonymousName &&
        anonymousPassword &&
        (!currentUserID || !cashedUserId)
      ) {
        cookieStore.setAnonymous(anonymousName, anonymousPassword);
      }
      // 방명록 수정
      await updateGuestbook({
        data: { ...commonNewData, anonymousName, anonymousPassword },
        collectionPath,
        docId: resolvedBoardId,
        notiType,
      });

      reset();
      setAddLoading(false);

      return;
    }
  }, [
    comment,
    boardId,
    currentUserID,
    commentAuthor,
    collectionPath,
    isAddDice,
    isSecret,
    commentId,
    guestbook,
    data,
    boardAuthorUid,
    anonymousName,
    anonymousPassword,
    allowAnonymous,
    commentType,
    myBattleResult,
    battleOpponentId,
    commentCollapseText,
    isCommentCollapse,
    commentImgURLList,
    isGmSpeak,
    sendToMastersAlert,
    notiType,
    isUseLuckyDraw,
    selectedLuckyDrawId,
    representativeCharacter,
    currentBattleMode,
  ]);

  const handleAddChat = useCallback(async () => {
    const resolvedBoardId = stableBoardIdRef.current || boardId || '';
    if (chat && currentUserID && resolvedBoardId && participants) {
      const processedChatComment = await processTimelineNumberTags(comment, collectionPath, false);
      const newData: IChat = {
        senderId: currentUserID,
        contents: processedChatComment,
        receiverIds: participants,
        type: CHAT_TYPE.DEFAULT,
      };
      reset();
      return addChat({
        data: newData,
        docId: resolvedBoardId,
      });
    }
  }, [
    comment,
    boardId,
    currentUserID,
    collectionPath,
    commentId,
    participants,
  ]);

  useEffect(() => {
    if (customCommentType) setCommentType(customCommentType);
  }, [customCommentType]);

  return (
    <AddCommentWrap>
      {isBattleMode && (
        <BattleMode
          collectionPath={collectionPath}
          setBattleResult={setBattleResult}
          setBattleOpponentId={setBattleOpponentId}
          battleMode={currentBattleMode}
        />
      )}

      {isCommentCollapse && (
        <AttatchmentWrapper>
          <strong>댓글 접기 문구</strong>
          <div>
            <TextInput
              placeholder="문구 입력"
              value={commentCollapseText}
              name="commentCollapseText"
              onChange={handleCommentCollapseTextChange}
              maxLength={120}
            />
          </div>
        </AttatchmentWrapper>
      )}

      {isUseLuckyDraw && hasStoreBoard && (
        <AttatchmentWrapper>
          <ItemSelector
            label="뽑기"
            direction="row"
            labelWidth="120px"
            align="center"
            collectionPath={CollectionPath.STORE}
            selectedIDList={selectedLuckyDrawId ? [selectedLuckyDrawId] : []}
            setSelectedIDList={(ids: string[]) =>
              setSelectedLuckyDrawId(ids[0] || '')
            }
            id={`${boardId}-randombox-selector`}
            selectOnlyOne={true}
            placeholder="뽑기 아이템 선택"
            myInventory={true}
            itemTypeListFilter={[ITEM_FUNCTION_TPYE.RANDOM_BOX_POST]}
          />
        </AttatchmentWrapper>
      )}
      <QuillEditorContainer
        maxHeight={maxHeight || 600}
        minHeight={minHeight || 60}
        style={style}
      >
        <EditorWrap type={type}>
          <Editor
            ref={quillRef}
            value={value || comment}
            onChange={handleEditorChange}
            placeholder={!isBattleMode ? placeholder : (currentBattleMode === BATTLE_MODE.CHALLENGE ? '도전장 한마디' : '배틀 한마디')}
            modules={modules}
            theme="snow"
            tabIndex={-1}
            onKeyDown={(e) => {
              if (newLineDisabled) handleEnterKeyPress(e, handleAddChat);
            }}
            style={{ maxHeight: maxHeight || 'auto' }}
          />
        </EditorWrap>

        {noControls && (
          <CustomToolBar
            setToolbar={mergedToolbar || initialToolbar}
            id={safeId}
            dir={dir}
            quillRef={quillRef}
            rows={toolbarRows}
            videoDropdownDirection="up"
          />
        )}

        {!noControls && setToolbar && (
          <>
            <ControlSec>
              <FrontBox>
                <CustomToolBar
                  setToolbar={mergedToolbar || initialToolbar}
                  id={safeId}
                  dir={dir || 'left'}
                  quillRef={quillRef}
                  style={{ width: 'auto' }}
                  videoDropdownDirection="up"
                />
              </FrontBox>
              <BackBox>
                <ControlBox>
                  {mergedCommentSetting.collapseComment &&
                    !guestbook && (
                      <Checkbox
                        value={`${boardId}-battle-checkbox`}
                        onChange={handleCommentCollapseChange}
                        checked={isCommentCollapse}
                      >
                        <CheckboxValue>댓글 접기</CheckboxValue>
                      </Checkbox>
                    )}

                  {mergedCommentSetting.secretCheckbox && (
                    <Checkbox
                      value={`${boardId}-secret-checkbox`}
                      onChange={toggleSecretChange}
                      checked={isSecret}
                    >
                      <CheckboxValue>비밀글</CheckboxValue>
                    </Checkbox>
                  )}
                  {mergedCommentSetting.battle && !isEdit && (
                    <Checkbox
                      value={`${boardId}-battle-checkbox`}
                      onChange={toggleBattleChange}
                      checked={isBattleMode}
                    >
                      <CheckboxValue>배틀</CheckboxValue>
                    </Checkbox>
                  )}
                  {mergedCommentSetting.dice && !isEdit && (
                    <Checkbox
                      value={`${boardId}-dice-checkbox`}
                      onChange={toggleDiceChange}
                      checked={isAddDice}
                    >
                      <CheckboxValue>주사위</CheckboxValue>
                    </Checkbox>
                  )}
                  {mergedCommentSetting.luckyDraw && hasStoreBoard && !isEdit && (
                    <Checkbox
                      value={`${boardId}-luckydraw-checkbox`}
                      onChange={() => setIsUseLuckyDraw(!isUseLuckyDraw)}
                      checked={isUseLuckyDraw}
                    >
                      <CheckboxValue>뽑기</CheckboxValue>
                    </Checkbox>
                  )}
                  {!(currentUserID && cashedUserId) && allowAnonymous && (
                    <>
                      <TextInput
                        name="anonymousName"
                        value={anonymousName}
                        onChange={handleAnonymousName}
                        placeholder="익명 닉네임"
                        style={{ width: '120px' }}
                      />
                      <TextInput
                        name="anonymousPassword"
                        value={anonymousPassword}
                        onChange={handleAnonymousName}
                        placeholder="비밀번호"
                        type="password"
                        style={{ width: '120px' }}
                      />
                    </>
                  )}
                </ControlBox>

                {/* 글자수 표시 */}
                {mergedCommentSetting.showCharacterCount && (
                  <CharacterCountWrapper isOverLimit={isOverLimit}>
                    <CharacterCount isOverLimit={isOverLimit}>
                      {currentCharacterCount.toLocaleString()}
                    </CharacterCount>
                    <span>/ {mergedCommentSetting.maxCharacterCount.toLocaleString()}자</span>
                  </CharacterCountWrapper>
                )}

                {isEdit ? (
                  <ButtonWrap>
                    <Button onClick={cancelEdit} shape="stroke" size="sm">
                      수정 취소
                    </Button>
                    <Button
                      onClick={() => {
                        if (isOverLimit) {
                          return alert(`글자수 제한(${mergedCommentSetting.maxCharacterCount.toLocaleString()}자)을 초과했습니다.`);
                        }
                        handleUpdateComment();
                      }}
                      size="sm"
                      disabled={isOverLimit}
                    >
                      수정 완료
                    </Button>
                  </ButtonWrap>
                ) : (
                  <Button
                    onClick={async () => {
                      // 📍 글자수 제한 체크
                      if (isOverLimit) {
                        return alert(`글자수 제한(${mergedCommentSetting.maxCharacterCount.toLocaleString()}자)을 초과했습니다.`);
                      }

                      // 🎯 커스텀 제출 핸들러가 있으면 우선 실행
                      if (onCustomSubmit) {
                        setAddLoading(true);
                        try {
                          const dice1 = isAddDice ? rollingDice() : 1;
                          const dice2 = isAddDice ? rollingDice() : 1;

                          const processedContent = await processTimelineNumberTags(comment, collectionPath, false);

                          await onCustomSubmit(processedContent, {
                            isSecret,
                            isAddDice,
                            dice: isAddDice ? [dice1, dice2] : undefined,
                            isBattleMode,
                            battleMode: currentBattleMode,
                            myBattleResult: myBattleResult || '',
                            battleOpponentId: battleOpponentId || '',
                            commentCollapseText: commentCollapseText || '',
                            isCommentCollapse,
                            commentImgURLList,
                            commentType,
                          });
                          reset();
                        } catch (error) {
                          console.error('❌ 커스텀 제출 실패:', error);
                        } finally {
                          setAddLoading(false);
                        }
                      } else if (chat) {
                        handleAddChat();
                      } else {
                        handleUpdateComment();
                      }
                    }}
                    pending={addLoading}
                    size={buttonSize || 'md'}
                    icon={submitIcon}
                    disabled={isOverLimit}
                  >
                    {submitText || '입력'}
                  </Button>
                )}
              </BackBox>
            </ControlSec>
          </>
        )}
      </QuillEditorContainer>
    </AddCommentWrap>
  );
};

export default CommentQuill;
